import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { AddressCheckButton } from "@/components/AddressCheckButton";
import { AddressFixDialog } from "@/components/AddressFixDialog";
import { AddressImport } from "@/components/AddressImport";
import { MapPin, Pencil } from "lucide-react";

const Stat = ({ label, value, cls, testId }) => (
  <div className="rounded-lg bg-slate-50 p-3" data-testid={testId}>
    <div className={`font-display text-2xl font-extrabold ${cls}`}>{value}</div>
    <div className="text-xs font-semibold text-slate-500">{label}</div>
  </div>
);

export const AddressHealthCard = () => {
  const [d, setD] = useState(null);
  const [fix, setFix] = useState(null);
  const close = useCallback(() => setFix(null), []);
  const load = useCallback(() => api.get("/admin/address-health").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const t = d.total;
  const pct = t.all ? Math.round((t.ready / t.all) * 100) : 0;
  return (
    <Card className="mb-6 p-5" data-testid="address-health-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-teal-700" />
          <h3 className="font-display font-bold text-slate-900">Mailing Address Health</h3>
          <span className="text-xs text-slate-500" data-testid="address-health-pct">{pct}% mail-ready</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AddressImport onDone={load} />
          <AddressCheckButton onDone={load} />
        </div>
      </div>
      <div className="mb-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Mail-ready" value={t.ready} cls="text-emerald-700" testId="address-health-ready" />
        <Stat label="Need address fix" value={t.fix} cls="text-red-600" testId="address-health-fix" />
        <Stat label="Not checked yet" value={t.unchecked} cls="text-slate-700" testId="address-health-unchecked" />
      </div>
      {d.by_mlo.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="address-health-by-mlo">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="py-1.5 pr-3">MLO</th><th className="py-1.5 pr-3 text-right">Ready</th><th className="py-1.5 pr-3 text-right">Fix</th><th className="py-1.5 text-right">Unchecked</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.by_mlo.map((m) => (
                <tr key={m.mlo_id}><td className="py-1.5 pr-3 text-slate-700">{m.name}</td><td className="py-1.5 pr-3 text-right tabular-nums">{m.ready}</td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${m.fix ? "font-bold text-red-600" : ""}`}>{m.fix}</td><td className="py-1.5 text-right tabular-nums">{m.unchecked}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.flagged.length > 0 && (
        <div className="mt-4" data-testid="address-health-flagged">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Leads needing a fix</div>
          {d.flagged.map((f) => (
            <button key={f.id} type="button" onClick={() => setFix(f)} data-testid={`fix-address-${f.id}`}
              className="group flex w-full items-start justify-between gap-2 border-b border-slate-100 py-1.5 text-left text-xs transition-colors hover:bg-slate-50">
              <span className="min-w-0">
                <span className="font-semibold text-slate-800">{f.name}</span> · {f.address} {f.mlo && <span className="text-slate-500">· {f.mlo}</span>}
                {f.error && <span className="block truncate text-red-600">{f.error}</span>}
              </span>
              <span className="flex flex-none items-center gap-1 font-semibold text-teal-700 group-hover:underline"><Pencil className="h-3 w-3" /> Fix</span>
            </button>
          ))}
        </div>
      )}
      <AddressFixDialog lead={fix} onClose={close} onFixed={load} />
    </Card>
  );
};
